import _ from 'lodash';
import moment from 'moment';
import api from '../api';
import translate from '../core/tools/translate';
import environment from '../environment';
import { CoreControl } from './CoreControl';
import { VectorType } from './VectorType';
import filters from '../core/filters';
import store from '../../store';
import worldMap from '../core/map';
import dataBoard from '../core/databoard';
import DataTable from '../core/charts/Datatable';
import numeral from 'numeral';
import Workspace from '../workspaces';
import { ControlType, Item } from '../workspaces/item';
import { ContextEnum } from '../../enuns/context.enum';
import { FeaturePermission } from '../../constants/feature.permission.helper';

const LayerInfo = (name) => require('../info').get(name);
const get = (name, currentMap = worldMap.getCurrent()) => {
    return require('../layers/index').default.get(name, currentMap);
};

const isAuthorized = (permission, feature) => api.authorization.isAuthorized(permission, feature);

export class MallCentersControl extends CoreControl {
    constructor () {
        const options = {
            id: 'mallCenters',
            name: 'MallCenters',
            filter: true,
            request: api.themes.get.mallCenters
        };

        super(options);

        this._changePeriod = this._changePeriod.bind(this);
        this._change = this._change.bind(this);
        this._toggleSales = this._toggleSales.bind(this);
        this._toggleLabel = this._toggleLabel.bind(this);
        this._setLabel = this._setLabel.bind(this);
        this.createFilter = this.createFilter.bind(this);
        this._filterCallback = this._filterCallback.bind(this);
        this.reload = this.reload.bind(this);
        this._getLayerOptions = this._getLayerOptions.bind(this);

        this._tags = {
            tagName: {
                property: 'MallName',
                align: 'east',
                size: 9,
                offsetY: 20,
                offsetX: -2,
                bold: true,
                box: true
            }
        };

        this.initPeriod();
        this.init();
    }


    static getInstance () {
        if (!MallCentersControl._instance) {
            MallCentersControl._instance = new MallCentersControl();
        }

        return MallCentersControl._instance;
    }

    init () {
        this.options.id = 'layer-mall-centers';
        const values = {
            help: translate(`Help.${this.options.name}`),
            id: 'layer-mall-centers',
            input: 'checkbox',
            label: translate('Common.MallCenters'),
            layer: this.getLayer,
            change: this._change,
            name: this.options.name,
            type: 'vector-points',
            hideClusterButton: true,
            filter: this.options.filter ? this.createFilter : null,
            overview: null,
            links: {},
            checkbox: {}
        };

        const formatDate = (date) => moment(date).format('MMM/YYYY');
        const { start, end } = this.period;

        values.checkbox[translate('Action.ShowAverageSalesAlt')] = {
            name: 'tagValue',
            change: this._toggleSales,
            link: {
                id: 'layer-mall-centers-date',
                name: 'MallCentersByYear',
                text: `${formatDate(start)} -> ${formatDate(end)}`,
                value: this.valueForDatePicker,
                change: async (checked, data) => {
                    await this._changePeriod(data);
                },
                type: 'datepicker'
            }
        };

        values.checkbox[translate('Action.ShowMallCenterName')] = {
            name: 'tagName',
            change: this._toggleLabel
        };

        if (!this._instance) {
            this._instance = new VectorType(values);
        }
    }

    get valueForDatePicker(){
        const years = environment.get('CONFIG.FILTERS.AVERAGE_SALES');
        return {
            ...this.period,
            max: environment.get('CONFIG.FILTERS.ENDYEAR'),
            min: new Date(years[years.length - 1],0,1),
        };
    }

    initPeriod(){
        const end = environment.get('CONFIG.FILTERS.ENDYEAR');
        const start = new Date(end.getFullYear(), 0, 1);
        this.period = { start, end };
    }

    get dateApiParams(){
        const adjustPeriod = (dt) => {
            const initialDate = dt, finalDate = new Date(dt.getTime()), month = finalDate.getMonth();
            return finalDate.getMonth() !== month ? finalDate : initialDate;
        };
        const apiDateTime = (date) => moment(date).format("YYYY/MM/DD");
        const {start, end} = this.period;
        return {
            initialDate: apiDateTime(start),
            finalDate: apiDateTime(adjustPeriod(end))
        };
    }

    _changePeriod (data) {
        this.period = data;
        api.general.log.action(ContextEnum.LayerMallCenters, 'Mudou Período', JSON.stringify(this.period));
        this.getLayer().setOptions({
            remote: {
                params: {
                    bandeiras: this.getLayer().options.remote.params.bandeiras,
                    ...this.dateApiParams,
                    bbox: 'LINESTRING(-13.095703125 5.266007882805511, -86.923828125 -33.46810795527895)'
                }
            }
        });

        if (this.check()) {
            this.getLayer().redraw();
        }
    }

    _change (e) {
        Workspace.addAction(new Item(`#${$(e).prop('id')}`, e.checked, ControlType.ICheckBox));
        api.general.log.action(ContextEnum.LayerMallCenters, e.checked ? 'enable' : 'disable');

        const layer = this.getLayer();
        if (e.checked) {
            if (!layer.isEmpty() && layer.samePosition()) {
                layer.reload();
            } else {
                layer.redraw();
            }
            this._setFilterButtonVisibility(true);
        } else {
            layer.hide();
            this._setFilterButtonVisibility(false);
        }
    }

    _getLayerTag () {
        const controls = this.getControls();
        const result = [];

        if (controls.checkbox && controls.checkbox.tagName && controls.checkbox.tagName.status) {
            result.push(_.assign({}, this._tags.tagName));
        }

        return result.length ? result : null;
    }

    _setLabel () {
        this.getLayer().setOptions({ tag: this._getLayerTag() });
    }

    async _rerender () {
        if (this.check() && this.getLayer().visible()) {
            await this.getLayer().reload();
        }
    }

    async _toggleSales (checked) {
        api.general.log.action(ContextEnum.LayerMallCenters, 'Show VMM', checked ? 'enable' : 'disable');
        // MediaMensal já vem na resposta do getmallcenterswithstores: basta
        // reprocessar as features em memória para o ícone trocar (sem refetch).
        await this._rerender();
    }

    async _toggleLabel (checked) {
        api.general.log.action(ContextEnum.LayerMallCenters, 'Show Label', checked ? 'enable' : 'disable');
        this._setLabel();
        await this._rerender();
    }

    getType () {
        const { params } = this.getLayer().options.remote;
        return params.locationType;
    }

    getTypeValue () {
        return this.getType();
    }

    getDate () {
        return this.period;
    }

    _setFilterButtonVisibility(state) {
        if (this.options.filter) {
            try {
                this.getControls().button.filter.input.enable(state);
                this.getControls().button.filter.enable = state;

                if (state) {
                    this.getControls().slider.range.input.enable(true);
                    this.getControls().slider.range.enable = true;
                    this.getControls().slider.range.input.value(3);
                    this.getControls().slider.range.value = 3;

                    this.createFilter();
                }

                if (!state) {
                    filters.close();
                }
            } catch (err) {
                console.log('ERROR: ', err);
            }
        }
    }

    createFilter() {
        filters.create({
            container: document.getElementById('filter-container'),
            id: `filter-${_.kebabCase(this.options.name)}-${this.getLayer().getMap()._id}`,
            label: translate(`Common.${this.options.name}`),
            type: 'mallCenters',
            direction: 'up',
            onSubmit: this._filterCallback,
            fields: {
                segments: true,
                brands: true,
                groups: true,
                fuelTypes: true,
            }
        });
    }

    _filterCallback() {
        if (this.check() && this.getLayer().visible()) {
            this.reload();
        }
    }

    reload() {
        if (this.check() && this.getLayer().visible()) {
            // Atualiza os params do filtro no remote e refaz a busca passando pelo
            // pipeline normal (success -> aggregateByMall). Sem isso, viriam features
            // de loja cruas (sem brands) e o popup/pin ficariam incorretos.
            this._getLayerOptions(this.getLayer().getMap()._id);
            this.getLayer().redraw();
        }
    }

    _getLayerOptions(id) {
        const filterData = filters.get(`filter-${_.kebabCase(this.options.name)}-${this.getLayer().getMap()._id}`, id) || {};
        const options = {
            bandeiras:  (() => {
                let flags = [];

                _.forEach(environment.get('CONFIG.DEALERS'), flag => flags.push(flag));
                _.forEach(environment.get('CONFIG.COMPETITORS'), flag => flags.push(flag));

                return flags.join(',');
            })(),
            ...this.dateApiParams,
            bbox: 'LINESTRING(-13.095703125 5.266007882805511, -86.923828125 -33.46810795527895)',
        };

        if (filterData.models) {
            options.idSegmentos = filterData.models.value;
        }

        if (filterData.brands) {
            options.bandeiras = filterData.brands.text.join(',');
        }

        if (filterData.group) {
            options.idGrupos = filterData.group.value;
        }

        if (filterData.fuelTypes) {
            options.idFuelTypes = filterData.fuelTypes.value;
        }

        if (filterData.industry && filterData.industry.value) {
            options.idIndustry = _.isArray(filterData.industry.value)
                ? filterData.industry.value.map(x => x.toString())
                : [filterData.industry.value.toString()];
        }

        if (filterData.mallCenters && filterData.mallCenters.value) {
            options.mallCenters = filterData.mallCenters.value;
        }

        if (filterData.state && filterData.state.value) {
            options.UF = filterData.state.value;
        }

        this.getLayer().setOptions({
            remote: {
                params: options
            }
        });

        return options;
    }

    _calculateSalesAverage(values){
        const {end, start} = this.period;
        const monthDiff = (d1, d2) => {
            let months = (d2.getFullYear() - d1.getFullYear()) * 12;
            months -= d1.getMonth() + 1;
            months += d2.getMonth() + 2;
            return months > 0 ? months : 1;
        };
        return _.sumBy(values, 'Total') /
           monthDiff(start, end);
    }
}

export default MallCentersControl;



// WEBPACK FOOTER //
// ./src/scripts/controls/MallCentersControl.js